import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Calendar, 
  MapPin, 
  Users, 
  Coffee, 
  Utensils, 
  Compass, 
  AlertTriangle, 
  Info, 
  ArrowRight, 
  Flag, 
  Sparkles, 
  Sun,
  ChevronRight
} from 'lucide-react';
import { Link } from 'react-router-dom';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { Helmet } from 'react-helmet-async';

const BegivenhederPage = () => {
  const [activeFilter, setActiveFilter] = useState('alle');

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Tournament schedule data
  const tournaments = [
    {
      date: '13. oktober',
      month: 'oktober',
      day: 'Tirsdag',
      course: 'Hills',
      fullName: 'Sancti Petri Hills Golf',
      description: 'En fantastisk og udfordrende bane designet af Manuel Piñero. Banen ligger smukt og byder på spændende greens og strategiske vandhazarder.',
      image: 'https://assets.auxodev.dk/assets/f3440563-2549-4ae9-b8e8-fd20a3984669/11475100-280d-40df-887e-b8b1083b3507.jpg',
      difficulty: 'Middel',
      holes: 18
    },
    {
      date: '15. oktober',
      month: 'oktober',
      day: 'Torsdag',
      course: 'Villa Nueva (ekstra)',
      fullName: 'Villanueva Golf Clinic',
      description: 'En ekstraordinær turnering på denne smukke parkbane. Banen er kendt for sine brede fairways og store, hurtige greens, der udfordrer dit puttespil.',
      image: 'https://assets.auxodev.dk/assets/f3440563-2549-4ae9-b8e8-fd20a3984669/e2629140-c2a2-40f1-a977-6c6dd158f23f.jpg',
      difficulty: 'Let / Middel',
      holes: 18,
      isExtra: true
    },
    {
      date: '20. oktober',
      month: 'oktober',
      day: 'Tirsdag',
      course: 'Estancia',
      fullName: 'La Estancia Golf',
      description: 'En naturskøn og flad bane, der ligger perfekt integreret i det andalusiske landskab. Her er der plads til alle niveauer, men vinden kan drille!',
      image: 'https://assets.auxodev.dk/assets/f3440563-2549-4ae9-b8e8-fd20a3984669/6b793ee2-bd69-4a5b-9d02-fda90a5c738a.jpg',
      difficulty: 'Middel',
      holes: 18
    },
    {
      date: '22. oktober',
      month: 'oktober',
      day: 'Torsdag',
      course: 'Fair Play',
      fullName: 'Fairplay Golf & Country Club',
      description: 'En absolut perle beliggende i naturskønne omgivelser med fantastiske udsigter over La Janda-dalen. En kuperet og spændende bane.',
      image: 'https://assets.auxodev.dk/assets/f3440563-2549-4ae9-b8e8-fd20a3984669/7eba72c7-1f45-4dbe-b026-ae1ff4b774ee.jpg',
      difficulty: 'Udfordrende',
      holes: 18
    },
    {
      date: '27. oktober',
      month: 'oktober',
      day: 'Tirsdag',
      course: 'Campano',
      fullName: 'Club de Golf Campano',
      description: 'En historisk og hyggelig bane omgivet af oliventræer, fyrretræer og smukke andalusiske villaer. Banens første 9 huller kræver stor præcision.',
      image: 'https://assets.auxodev.dk/assets/f3440563-2549-4ae9-b8e8-fd20a3984669/aba0a291-34d3-46b4-8f1c-14705886b276.jpg',
      difficulty: 'Middel',
      holes: 18
    },
    {
      date: '3. november',
      month: 'november',
      day: 'Tirsdag',
      course: 'Hills',
      fullName: 'Sancti Petri Hills Golf',
      description: 'Vi vender tilbage til den populære Hills-bane. Perfekt mulighed for at forbedre din score fra sidst og nyde det gode selskab.',
      image: 'https://assets.auxodev.dk/assets/f3440563-2549-4ae9-b8e8-fd20a3984669/8d7de451-2291-4dda-856d-0c4c68d77b2f.jpg',
      difficulty: 'Middel',
      holes: 18
    },
    {
      date: '5. november',
      month: 'november',
      day: 'Torsdag',
      course: 'Estancia',
      fullName: 'La Estancia Golf',
      description: 'Endnu en runde på La Estancia. Banen præsenterer sig fantastisk i november, hvor temperaturen er yderst behagelig til golf.',
      image: 'https://assets.auxodev.dk/assets/f3440563-2549-4ae9-b8e8-fd20a3984669/8f7178f9-f1d3-4173-83ca-8116b639a6a2.jpg',
      difficulty: 'Middel',
      holes: 18
    },
    {
      date: '10. november',
      month: 'november',
      day: 'Tirsdag',
      course: 'Eventuelt turnering',
      fullName: 'Valgfri bane / Ekstra runde',
      description: 'Vi holder denne dag åben til en eventuel ekstra turnering, en hyggelig "opsamlingsrunde" eller spil på en af områdets øvrige perler efter gæsternes ønsker.',
      image: 'https://assets.auxodev.dk/assets/f3440563-2549-4ae9-b8e8-fd20a3984669/e5662521-3e98-4e58-97d2-2cd8f537c459.jpg',
      difficulty: 'Efter aftale',
      holes: 18,
      isTBD: true
    }
  ];

  // Common activities data
  const commonActivities = [
    {
      title: 'Frokost i Muela',
      date: '17. oktober & 14. november',
      description: 'Vi mødes og kører i fællesskab op til den utroligt hyggelige bjergby La Muela. Her spiser vi en fantastisk, traditionel andalusisk frokost på en lokal restaurant, nyder den storslåede udsigt og det gode selskab.',
      icon: Utensils,
      bgClass: 'bg-amber-50 border-amber-100'
    },
    {
      title: 'Daglige møder på strandbaren',
      date: 'Hver dag kl. 17.00',
      description: 'Efter dagens strabadser på golfbanen mødes vi uformelt på strandbaren (chiringuitoen) omkring kl. 17.00. Her deler vi dagens gode slag, griner af de mindre gode og nyder en kold forfriskning, mens solen sænker sig over Atlanterhavet.',
      icon: Coffee,
      bgClass: 'bg-blue-50 border-blue-100'
    },
    {
      title: 'Fælles middag én gang om ugen',
      date: 'Ofte fredag eller lørdag',
      description: 'Vi arrangerer en hyggelig fællesmiddag én gang om ugen på en nøje udvalgt restaurant i La Barrosa eller omegn. Det er altid en festlig aften med god mad, spansk rødvin og masser af røverhistorier fra golfbanen.',
      icon: Users,
      bgClass: 'bg-emerald-50 border-emerald-100'
    },
    {
      title: 'Lokale oplevelser & udflugter',
      date: 'Løbende under opholdet',
      description: 'Hvis der sker noget spændende i nærområdet – det kunne være et lokalt marked, en vinfestival, flamenco-optræden eller en koncert – giver vi besked til alle gæster, så de, der har lyst, kan tage afsted sammen.',
      icon: Compass,
      bgClass: 'bg-purple-50 border-purple-100'
    }
  ];

  // Holidays data
  const holidays = [
    {
      date: '12. oktober',
      name: 'Fiesta Nacional de España (Spaniens Nationaldag)',
      description: 'En stor national helligdag i hele Spanien. Vær opmærksom på, at de fleste supermarkeder og almindelige butikker holder lukket denne dag, mens restauranter og strandbarer ofte har travlt og masser af liv.'
    },
    {
      date: '2. november',
      name: 'Allerhelgensdag (Flyttet)',
      description: 'Da den officielle Allerhelgensdag (1. november) falder på en søndag i 2026, er helligdagen flyttet til mandag den 2. november. Offentlige kontorer, banker og mange butikker vil være lukkede denne dag.'
    }
  ];

  const filteredTournaments = activeFilter === 'alle' 
    ? tournaments 
    : tournaments.filter(t => t.month === activeFilter);

  return (
    <div className="min-h-screen bg-slate-50">
      <Helmet>
        <title>Begivenheder i La Barrosa – Efterår 2026 | Golf4Life</title>
        <meta name="description" content="Se det foreløbige program for efterårssæsonen 2026 i La Barrosa. Få overblik over turneringsplanen, fælles aktiviteter, frokoster i Muela og andalusiske helligdage." />
      </Helmet>

      <Navbar solid={true} />

      <main className="pt-20 md:pt-24">
        {/* Hero Section */}
        <section className="relative bg-slate-900 text-white py-20 md:py-28 overflow-hidden">
          <div className="absolute inset-0 z-0 opacity-40">
            <img 
              src="https://assets.auxodev.dk/assets/f3440563-2549-4ae9-b8e8-fd20a3984669/397666f3-e4fa-4a9c-bd59-49645054de02.jpg" 
              alt="Golfbane i Andalusien" 
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-900/90 to-transparent" />
          </div>

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="max-w-3xl">
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[var(--brand)] text-white text-xs font-semibold uppercase tracking-wider mb-6">
                <Sparkles size={14} /> Efterårssæsonen 2026
              </span>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight text-white mb-6">
                Begivenheder i La Barrosa – efterår 2026
              </h1>
              <p className="text-lg md:text-xl text-slate-200 leading-relaxed mb-8">
                Planlægningen af efterårssæsonen 2026 er i fuld gang! Som gæst hos Golf4Life kan du allerede nu se den foreløbige turneringsplan og vores hyggelige fælles aktiviteter, så du kan glæde dig og planlægge dit ophold.
              </p>
              
              <div className="mb-8 flex flex-wrap gap-4">
                <Link 
                  to="/kontakt" 
                  className="inline-flex items-center justify-center px-6 py-3 md:px-8 md:py-4 rounded-full border-2 border-white bg-transparent text-white font-semibold text-base md:text-lg hover:-translate-y-1 transition-all shadow-lg shadow-black/20 group"
                >
                  Book dit ophold
                  <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
              
              {/* Warning/Notification Box */}
              <div className="flex items-start gap-4 p-4 bg-white/10 backdrop-blur-md rounded-2xl border border-white/15">
                <Info className="text-[var(--brand)] shrink-0 mt-0.5" size={20} />
                <p className="text-sm text-slate-200 leading-relaxed">
                  <strong>Bemærk:</strong> Dette er en foreløbig plan. Der kan forekomme løbende ændringer og justeringer i programmet. Skulle der ske ændringer, får du naturligvis direkte besked i god tid under dit ophold.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Tournament Schedule Section */}
        <section className="py-16 md:py-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
            <h2 className="text-3xl md:text-4xl font-semibold text-slate-900 mb-4">
              Turneringsplanen
            </h2>
            <p className="text-lg text-slate-600">
              Glæd dig til spændende og hyggelige runder på nogle af Andalusiens smukkeste golfbaner. Vores turneringer har altid det sociale samvær i fokus.
            </p>

            {/* Month Filter Buttons */}
            <div className="flex justify-center gap-2 mt-8 p-1 bg-slate-200/60 rounded-full max-w-xs mx-auto">
              <button
                onClick={() => setActiveFilter('alle')}
                className={`flex-1 py-2 px-4 rounded-full text-sm font-medium transition-all ${
                  activeFilter === 'alle' 
                    ? 'bg-white text-slate-900 shadow-sm' 
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                Alle
              </button>
              <button
                onClick={() => setActiveFilter('oktober')}
                className={`flex-1 py-2 px-4 rounded-full text-sm font-medium transition-all ${
                  activeFilter === 'oktober' 
                    ? 'bg-white text-slate-900 shadow-sm' 
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                Oktober
              </button>
              <button
                onClick={() => setActiveFilter('november')}
                className={`flex-1 py-2 px-4 rounded-full text-sm font-medium transition-all ${
                  activeFilter === 'november' 
                    ? 'bg-white text-slate-900 shadow-sm' 
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                November
              </button>
            </div>
          </div>

          {/* Timeline / Tournament List */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
            {/* Left 2 columns: The list */}
            <div className="lg:col-span-2 space-y-6">
              {filteredTournaments.map((tournament, index) => (
                <motion.div
                  key={tournament.date}
                  initial={{ opacity: 0, y: 15 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.05 }}
                  className={`bg-white rounded-2xl p-6 shadow-sm border transition-all hover:shadow-md flex flex-col sm:flex-row gap-6 items-start ${
                    tournament.isExtra ? 'border-amber-200 bg-amber-50/10' : 'border-slate-100'
                  }`}
                >
                  {/* Date Badge */}
                  <div className="flex sm:flex-col items-center justify-center bg-slate-100 rounded-xl p-3 text-center shrink-0 min-w-[100px] w-full sm:w-auto border border-slate-200/50">
                    <span className="text-xs font-semibold uppercase text-slate-500 tracking-wider">
                      {tournament.day}
                    </span>
                    <span className="text-xl font-bold text-slate-900 sm:my-1">
                      {tournament.date.split(' ')[0]}
                    </span>
                    <span className="text-xs font-medium text-slate-600">
                      {tournament.date.split(' ')[1]}
                    </span>
                  </div>

                  {/* Details */}
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <h3 className="text-xl font-semibold text-slate-900">
                        {tournament.course}
                      </h3>
                      {tournament.isExtra && (
                        <span className="px-2.5 py-0.5 rounded-full bg-amber-100 text-amber-800 text-xs font-semibold">
                          Ekstra turnering
                        </span>
                      )}
                      {tournament.isTBD && (
                        <span className="px-2.5 py-0.5 rounded-full bg-blue-100 text-blue-800 text-xs font-semibold">
                          Valgfri / Eventuel
                        </span>
                      )}
                    </div>
                    
                    <p className="text-sm font-medium text-[var(--brand)] mb-3 flex items-center gap-1">
                      <MapPin size={14} /> {tournament.fullName}
                    </p>
                    
                    <p className="text-sm text-slate-600 leading-relaxed mb-4">
                      {tournament.description}
                    </p>

                    {/* Meta info */}
                    <div className="flex flex-wrap gap-4 text-xs text-slate-500 border-t border-slate-100 pt-3">
                      <span className="flex items-center gap-1">
                        <Flag size={14} className="text-slate-400" /> {tournament.holes} Huller
                      </span>
                      <span className="flex items-center gap-1">
                        <Sparkles size={14} className="text-slate-400" /> Sværhedsgrad: {tournament.difficulty}
                      </span>
                    </div>
                  </div>

                  {/* Course Thumbnail */}
                  <div className="w-full sm:w-32 h-32 rounded-xl overflow-hidden shrink-0 border border-slate-100">
                    <img 
                      src={tournament.image} 
                      alt={tournament.fullName} 
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Right column: Info & Highlights */}
            <div className="space-y-6">
              <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
                <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <Flag className="text-[var(--brand)]" size={20} /> Banerne vi spiller
                </h3>
                <p className="text-sm text-slate-600 leading-relaxed mb-4">
                  Vi har nøje udvalgt baner, der både udfordrer den øvede golfer og er en fornøjelse for spillere med et højere handicap.
                </p>
                <ul className="space-y-3 text-sm text-slate-700">
                  <li className="flex items-start gap-2">
                    <ChevronRight size={16} className="text-[var(--brand)] shrink-0 mt-0.5" />
                    <span><strong>Sancti Petri Hills:</strong> Meget populær og hyggelig parkbane lige i baghaven.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <ChevronRight size={16} className="text-[var(--brand)] shrink-0 mt-0.5" />
                    <span><strong>La Estancia:</strong> Flad og naturskøn med fantastiske andalusiske oliventræer.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <ChevronRight size={16} className="text-[var(--brand)] shrink-0 mt-0.5" />
                    <span><strong>Fair Play:</strong> Enestående udsigter og kuperet terræn, der kræver omtanke.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <ChevronRight size={16} className="text-[var(--brand)] shrink-0 mt-0.5" />
                    <span><strong>Campano:</strong> Præcision og strategi er nøglen på denne historiske bane.</span>
                  </li>
                </ul>
              </div>

              {/* Quick Contact Box */}
              <div className="bg-[var(--brand)] text-white rounded-2xl p-6 shadow-md relative overflow-hidden">
                <div className="absolute -right-10 -bottom-10 opacity-10">
                  <Calendar size={180} />
                </div>
                <h3 className="text-xl font-semibold mb-2 relative z-10">Har du spørgsmål?</h3>
                <p className="text-sm text-white/90 mb-6 leading-relaxed relative z-10">
                  Vil du vide mere om turneringerne, handicapkrav eller det praktiske omkring spillet? Ring eller skriv til os.
                </p>
                <a 
                  href="/kontakt" 
                  className="inline-flex items-center gap-2 bg-white text-slate-900 px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-slate-50 transition-all shadow-sm relative z-10"
                >
                  Kontakt os <ArrowRight size={16} />
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Common Activities Section */}
        <section className="bg-slate-100 py-16 md:py-24 border-y border-slate-200/50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
              <span className="text-sm font-semibold text-[var(--brand)] uppercase tracking-wider block mb-2">
                Det sociale fællesskab
              </span>
              <h2 className="text-3xl md:text-4xl font-semibold text-slate-900 mb-4">
                Fælles hygge og arrangementer
              </h2>
              <p className="text-lg text-slate-600">
                Hos Golf4Life rejser vi sammen og hygger os sammen. Udover golfen lægger vi stor vægt på det sociale fællesskab, hvor der altid er plads til alle.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {commonActivities.map((activity, index) => {
                const IconComponent = activity.icon;
                return (
                  <motion.div
                    key={activity.title}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className={`bg-white rounded-2xl p-8 shadow-sm border border-slate-200/60 flex gap-6 items-start`}
                  >
                    <div className={`p-4 rounded-xl shrink-0 ${activity.bgClass} border`}>
                      <IconComponent size={28} className="text-slate-800" />
                    </div>
                    <div>
                      <span className="text-xs font-semibold uppercase tracking-wider text-[var(--brand)] block mb-1">
                        {activity.date}
                      </span>
                      <h3 className="text-xl font-semibold text-slate-900 mb-3">
                        {activity.title}
                      </h3>
                      <p className="text-slate-600 text-sm leading-relaxed">
                        {activity.description}
                      </p>
                    </div>
                  </motion.div>
                );
              })}
            </div>

            {/* Quote / Atmosphere Callout */}
            <div className="mt-12 bg-white rounded-2xl p-6 md:p-8 shadow-sm border border-slate-200/60 flex flex-col md:flex-row items-center gap-6">
              <div className="flex-1 text-center md:text-left">
                <p className="text-slate-700 italic text-base leading-relaxed mb-2">
                  "Det sociale er mindst lige så vigtigt som golfen. Vi elsker at samle vores gæster til en kold drink på strandbaren eller en hyggelig frokost i bjergene, hvor snakken går lystigt på kryds og tværs."
                </p>
                <span className="text-sm font-semibold text-slate-900">— Niels & hustru, jeres værter i La Barrosa</span>
              </div>
            </div>
          </div>
        </section>

        {/* Holidays Section */}
        <section className="py-16 md:py-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-3xl p-8 md:p-12 shadow-sm border border-slate-100 max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 bg-amber-50 rounded-xl text-amber-600 border border-amber-100">
                <Sun size={24} />
              </div>
              <div>
                <h2 className="text-2xl md:text-3xl font-semibold text-slate-900">
                  Helligdage i Andalusien
                </h2>
                <p className="text-sm text-slate-500">Efterår 2026</p>
              </div>
            </div>

            <p className="text-slate-600 mb-8 leading-relaxed">
              I Spanien og særligt i Andalusien fejres helligdage med stor stolthed. Det er rart at kende disse datoer på forhånd, da det kan have betydning for åbningstider i supermarkeder og butikker.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {holidays.map((holiday) => (
                <div key={holiday.date} className="p-5 rounded-2xl bg-slate-50 border border-slate-100 relative overflow-hidden">
                  <div className="absolute top-0 left-0 w-1.5 h-full bg-[var(--brand)]" />
                  <span className="inline-block px-2.5 py-1 rounded-md bg-white text-slate-900 border border-slate-200/50 text-xs font-bold mb-3">
                    {holiday.date}
                  </span>
                  <h3 className="text-base font-semibold text-slate-900 mb-2">
                    {holiday.name}
                  </h3>
                  <p className="text-slate-600 text-xs leading-relaxed">
                    {holiday.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="bg-slate-900 text-white py-16 md:py-20 relative overflow-hidden">
          <div className="absolute inset-0 opacity-20">
            <img 
              src="https://assets.auxodev.dk/assets/f3440563-2549-4ae9-b8e8-fd20a3984669/88393245-95da-4648-8ed7-33384daecaa2.jpg" 
              alt="Golfbane i Cádiz" 
              className="w-full h-full object-cover"
            />
          </div>
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
            <h2 className="text-3xl md:text-4xl font-semibold tracking-tight mb-4">
              Vil du med til efteråret 2026?
            </h2>
            <p className="text-lg text-slate-300 max-w-2xl mx-auto mb-8 leading-relaxed">
              Vores longstay-golfophold i det andalusiske efterår er utroligt populære, og pladserne bliver hurtigt fyldt op. Kontakt os i dag for at høre mere om ledige lejligheder og priser.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a 
                href="/kontakt" 
                className="inline-flex items-center justify-center px-8 py-4 rounded-full bg-[var(--brand)] text-white font-semibold text-lg hover:-translate-y-1 transition-all shadow-lg"
              >
                Kontakt os nu
              </a>
              <a 
                href="/ophold" 
                className="inline-flex items-center justify-center px-8 py-4 rounded-full bg-white/10 backdrop-blur-md text-white border border-white/20 font-semibold text-lg hover:bg-white/20 transition-all"
              >
                Se vores ophold
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default BegivenhederPage;
